import React, { useRef, useState } from 'react';
import { Button } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import moment from 'moment';
import useGeneratePDF from '../../hooks/useGeneratePDF';
import '../../styles/reports.scss';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TripLocation {
  waypoint_index: number;
  location_id?: number;
  input_location_name?: string;
  input_address?: string;
  delivery_status?: string;
  delivery_date_time?: string;
  delivery_remark?: string;
  delivery_value?: string | number | null;
  value?: number;
  value_entries?: Record<string, number>;
  srn_value?: string | number | null;
  items?: Record<string, number> | any[];
  comments?: string | null;
  waiting_time?: number;
  input_start_row?: string;
}

interface TripDetails {
  trip_id: number;
  trip_name?: string;
  trip_status?: string;
  driver_name?: string;
  vehicle_number?: string;
  number_of_locations?: number;
  actual_distance?: number;
  optimized_distance?: number;
  unoptimized_distance?: number;
  delivery_start?: string;
  delivery_end?: string;
  optimized_date?: string;
  carbon_saving?: number;
  locations?: TripLocation[];
}

interface CollectionSummary {
  totalValue: number;
  totalDelivered: number;
  totalDeliveryReturn: number;
  remaining: number;
  collectionByType: Record<string, number>;
}

interface TripDownloadButtonProps {
  tripId: number;
  licenseNo?: string;
  fetchTripDetails: (tripId: number, licenseNo: string) => Promise<TripDetails>;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const FINANCIAL_TYPES = ['cash', 'cheque'];

const isFinancialType = (type: string): boolean => FINANCIAL_TYPES.some((t) => type.toLowerCase().includes(t));

const VALUE_TYPE_CONFIG: Record<string, { color: string; tagColor: string; label: string }> = {
  cash: { color: '#52c41a', tagColor: 'green', label: 'Cash' },
  cheque: { color: '#fa8c16', tagColor: 'orange', label: 'Cheque' },
  unit: { color: '#888', tagColor: 'default', label: 'Units' },
  card: { color: '#1890ff', tagColor: 'blue', label: 'Card' },
};

const getTypeConfig = (type: string) =>
  VALUE_TYPE_CONFIG[type.toLowerCase()] ?? { color: '#888', tagColor: 'default', label: capitalize(type) };

const STATUS_COLORS: Record<string, string> = {
  delivered: '#27ae60',
  skipped: '#e67e22',
  cancelled: '#c0392b',
  planned: '#2980b9',
  unplanned: '#95a5a6',
};

// ── Pure helpers ──────────────────────────────────────────────────────────────

const fmtDate = (val?: string | null): string =>
  val && moment(val).isValid() ? moment(val).format('YYYY-MM-DD HH:mm') : '—';

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const renderItems = (items?: Record<string, number> | any[]): string => {
  if (!items) return '—';
  if (Array.isArray(items)) {
    if (items.length === 0) return '—';
    return items
      .map((item: any) => `${Number(item.capacity) < 0 ? '↓' : '↑'} ${item.item_name} (${Math.abs(item.capacity)})`)
      .join(', ');
  }
  const entries = Object.entries(items);
  if (entries.length === 0) return '—';
  return entries.map(([name, qty]) => `${Number(qty) < 0 ? '↓' : '↑'} ${name} (${Math.abs(Number(qty))})`).join(', ');
};

const renderDeliveredValue = (
  entries: Record<string, number> | undefined,
  deliveryValue: string | number | null | undefined,
  deliveryStatus?: string,
): React.ReactElement => {
  if (deliveryStatus !== 'delivered') {
    return <span style={{ color: '#aaa' }}>—</span>;
  }

  const entryPairs = Object.entries(entries ?? {});
  const total = Number(deliveryValue ?? entryPairs.reduce((s, [, amt]) => s + Number(amt), 0));

  if (entryPairs.length === 0) {
    return <span>{total > 0 ? total.toFixed(2) : '—'}</span>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {entryPairs.map(([type, amount]) => {
        const cfg = getTypeConfig(type);
        return (
          <div key={type}>
            <span
              style={{
                border: `1px solid ${cfg.color}`,
                color: cfg.color,
                borderRadius: 3,
                padding: '1px 6px',
                fontSize: 9,
                marginRight: 4,
              }}
            >
              {cfg.label}
            </span>
            <span style={{ color: cfg.color, fontWeight: 600 }}>{Number(amount).toFixed(2)}</span>
          </div>
        );
      })}
      <div style={{ borderTop: '1px solid #eee', marginTop: 2, paddingTop: 2, fontSize: 10, color: '#888' }}>
        Total: <span style={{ color: '#333', fontWeight: 600 }}>{total.toFixed(2)}</span>
      </div>
    </div>
  );
};

const renderDeliveryReturn = (loc: TripLocation): React.ReactElement => {
  if (loc.delivery_status !== 'delivered') {
    return <span style={{ color: '#95a5a6' }}>—</span>;
  }
  const entries = Object.entries(loc.value_entries ?? {});
  const deliveredTotal =
    entries.length > 0 ? entries.reduce((sum, [, amount]) => sum + Number(amount), 0) : Number(loc.delivery_value ?? 0);

  const returnValue = Number(loc.value ?? 0) - deliveredTotal;

  return <span style={{ color: '#c0392b', fontWeight: 600 }}>{returnValue.toFixed(2)}</span>;
};

// ── Collection summary calculation (single pass) ──────────────────────────────

const buildCollectionSummary = (locations: TripLocation[]): CollectionSummary => {
  const collectionByType: Record<string, number> = {};
  let totalValue = 0;
  let totalDelivered = 0;
  let totalDeliveryReturn = 0;

  locations.forEach((loc) => {
    totalValue += Number(loc.value ?? 0);
    if (loc.delivery_status === 'delivered') {
      const entries = Object.entries(loc.value_entries ?? {});
      const deliveredTotal =
        entries.length > 0
          ? entries.reduce((sum, [, amount]) => sum + Number(amount), 0)
          : Number(loc.delivery_value ?? 0);

      totalDeliveryReturn += Number(loc.value ?? 0) - deliveredTotal;

      entries.forEach(([value_type, amount]) => {
        const key = value_type.toLowerCase();
        collectionByType[key] = (collectionByType[key] ?? 0) + Number(amount);
        if (isFinancialType(key)) {
          totalDelivered += Number(amount);
        }
      });
    }
  });

  return {
    totalValue,
    totalDelivered,
    totalDeliveryReturn,
    remaining: totalValue - totalDelivered,
    collectionByType,
  };
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionTitle = ({ title, color }: { title: string; color?: string }) => (
  <div className="report__section-title" style={{ '--section-color': color ?? '#2980b9' } as React.CSSProperties}>
    {title}
  </div>
);

SectionTitle.defaultProps = { color: '#2980b9' };

const InfoGrid = ({ items, bg }: { items: [string, string][]; bg?: string }) => (
  <div className="report__grid report__grid--mb24" style={{ gridTemplateColumns: '1fr 1fr' }}>
    {items.map(([label, value]) => (
      <div key={label} className="report__row">
        <div
          className="report__cell-label report__cell-label--w45"
          style={{ '--section-bg': bg ?? '#d6eaf8' } as React.CSSProperties}
        >
          {label}
        </div>
        <div className="report__cell-value report__cell-value--w55">{value}</div>
      </div>
    ))}
  </div>
);

InfoGrid.defaultProps = { bg: '#d6eaf8' };

const CollectionCard = ({
  label,
  value,
  bg,
  valueColor,
  bold,
}: {
  label: string;
  value: string;
  bg: string;
  valueColor?: string;
  bold?: boolean;
}) => (
  <div style={{ background: bg, padding: '8px 14px', borderRadius: 4, minWidth: 120 }}>
    <div style={{ fontSize: 10, color: '#888' }}>{label}</div>
    <div style={{ fontWeight: bold ? 700 : 600, color: valueColor }}>{value}</div>
  </div>
);

CollectionCard.defaultProps = { valueColor: undefined, bold: false };

// ── Report Template ───────────────────────────────────────────────────────────

const TripReportTemplate = React.forwardRef<HTMLDivElement, { trip: TripDetails }>(({ trip }, ref) => {
  const sortedLocations = [...(trip.locations ?? [])].sort((a, b) => a.waypoint_index - b.waypoint_index);

  const summary = buildCollectionSummary(sortedLocations);

  const tripSummaryItems: [string, string][] = [
    ['Trip ID', String(trip.trip_id)],
    ['Trip Name', trip.trip_name ?? 'N/A'],
    ['Status', trip.trip_status ?? 'N/A'],
    ['Driver', trip.driver_name ?? 'N/A'],
    ['Vehicle', trip.vehicle_number ?? 'N/A'],
    ['Total Locations', String(trip.number_of_locations ?? 'N/A')],
    ['Optimized Date', fmtDate(trip.optimized_date)],
    ['Delivery Start', fmtDate(trip.delivery_start)],
    ['Delivery End', fmtDate(trip.delivery_end)],
    ['Actual Distance', `${(trip.actual_distance ?? 0).toFixed(2)} Km`],
    ['Optimized Distance', `${(trip.optimized_distance ?? 0).toFixed(2)} Km`],
    ['Unoptimized Distance', `${(trip.unoptimized_distance ?? 0).toFixed(2)} Km`],
  ];

  const tableHeaders = [
    '#',
    'Location',
    'Address',
    'Items',
    'Value',
    'Delivered Value',
    'Delivery Return',
    'S.R.N Value',
    'Delivery Status',
    'Delivery Date',
    'Remark',
    'Comment',
  ];

  return (
    <div
      ref={ref}
      className="report report--w1200"
      style={{ '--report-accent': '#2980b9', fontSize: '11px' } as React.CSSProperties}
    >
      {/* Header */}
      <div className="report__header">
        <span className="report__title">Trip Report — {trip.trip_name ?? `Trip ${trip.trip_id}`}</span>
        <span className="report__generated">Generated: {new Date().toLocaleString()}</span>
      </div>

      {/* Trip Summary */}
      <SectionTitle title="Trip Summary" />
      <InfoGrid items={tripSummaryItems} />

      {/* Delivery Collections Summary */}
      <SectionTitle title="Delivery Collection Summary" />
      <table
        className="report__summary-table"
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: 16,
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          <col style={{ width: '25%' }} />
          <col style={{ width: '25%' }} />
          <col style={{ width: '25%' }} />
          <col style={{ width: '25%' }} />
        </colgroup>
        <tbody>
          <tr>
            <td className="report__summary-label">Total Delivery Value</td>
            <td className="report__summary-value">{summary.totalValue.toFixed(2)}</td>
            <td className="report__summary-label">Remaining Value</td>
            <td className="report__summary-value" style={{ color: summary.remaining > 0 ? '#f5222d' : '#52c41a' }}>
              {summary.remaining.toFixed(2)}
            </td>
          </tr>
          <tr>
            <td className="report__summary-label">Total Delivery Return</td>
            <td
              className="report__summary-value"
              style={{ color: summary.totalDeliveryReturn > 0 ? '#f5222d' : '#52c41a' }}
            >
              {summary.totalDeliveryReturn.toFixed(2)}
            </td>
            <td className="report__summary-label">Total Collected</td>
            <td className="report__summary-value" style={{ color: '#1890ff' }}>
              {summary.totalDelivered.toFixed(2)}
            </td>
          </tr>
          {(() => {
            const entries = Object.entries(summary.collectionByType);
            const rows: [string, number][][] = [];
            for (let i = 0; i < entries.length; i += 2) {
              rows.push(entries.slice(i, i + 2) as [string, number][]);
            }
            return rows.map((rowEntries) => {
              const rowKey = rowEntries.map(([type]) => type).join('-');
              return (
                <tr key={rowKey}>
                  {rowEntries.map(([type, amount]) => {
                    const cfg = getTypeConfig(type);
                    const isUnit = !isFinancialType(type);
                    return (
                      <React.Fragment key={type}>
                        <td className="report__summary-label">
                          <span
                            style={{
                              border: `1px solid ${cfg.color}`,
                              color: cfg.color,
                              borderRadius: 3,
                              padding: '1px 6px',
                              fontSize: 10,
                              marginRight: 6,
                            }}
                          >
                            {cfg.label}
                          </span>
                          {isUnit ? 'Delivered' : 'Collected'}
                        </td>
                        <td className="report__summary-value" style={{ color: cfg.color }}>
                          {amount.toFixed(2)}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  {rowEntries.length === 1 && (
                    <>
                      <td className="report__summary-label" aria-hidden="true" />
                      <td className="report__summary-value" aria-hidden="true" />
                    </>
                  )}
                </tr>
              );
            });
          })()}
        </tbody>
      </table>

      {/* Location Details */}
      <SectionTitle title="Location Details" />
      <table className="report__table report__table--fs10">
        <thead>
          <tr className="report__thead-row">
            {tableHeaders.map((h) => (
              <th key={h} className="report__th report__th--pad7">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedLocations.map((loc, index) => {
            let label: string;
            if (index === 0) {
              label = 'Start';
            } else if (index === sortedLocations.length - 1) {
              label = 'End';
            } else {
              label = String(index);
            }
            return (
              <tr key={loc.location_id ?? index} className={index % 2 === 0 ? 'report__tr--even' : 'report__tr--odd'}>
                <td className="report__td report__td--pad6sm report__td--bold">{label}</td>
                <td className="report__td report__td--pad6sm">{loc.input_location_name ?? 'N/A'}</td>
                <td className="report__td report__td--pad6sm">{loc.input_address ?? 'N/A'}</td>
                <td className="report__td report__td--pad6sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {renderItems(loc.items)}
                </td>
                <td className="report__td report__td--pad6sm">{(loc.value ?? 0).toFixed(2)}</td>
                <td className="report__td report__td--pad6sm">
                  {renderDeliveredValue(loc.value_entries, loc.delivery_value, loc.delivery_status)}
                </td>
                <td className="report__td report__td--pad6sm">{renderDeliveryReturn(loc)}</td>
                <td className="report__td report__td--pad6sm">{loc.srn_value ?? '—'}</td>
                <td
                  className="report__td report__td--pad6sm report__td--bold"
                  style={{ color: STATUS_COLORS[loc.delivery_status ?? ''] ?? '#95a5a6' }}
                >
                  {loc.delivery_status ?? 'unplanned'}
                </td>
                <td className="report__td report__td--pad6sm">{fmtDate(loc.delivery_date_time)}</td>
                <td className="report__td report__td--pad6sm">{loc.delivery_remark ?? '—'}</td>
                <td className="report__td report__td--pad6sm">{loc.comments ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Footer */}
      <div className="report__footer">
        <span>RouteSonar — Trip {trip.trip_id}</span>
        <span>{new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );
});

TripReportTemplate.displayName = 'TripReportTemplate';

// ── Main Component ────────────────────────────────────────────────────────────

const TripDetailsReport = ({ tripId, licenseNo = '', fetchTripDetails }: TripDownloadButtonProps): JSX.Element => {
  const templateRef = useRef<HTMLDivElement>(null);
  const [trip, setTrip] = useState<TripDetails | null>(null);
  const [fetching, setFetching] = useState(false);

  const { generate, loading } = useGeneratePDF(templateRef, {
    fileName: `trip_${tripId}_report.pdf`,
    orientation: 'landscape',
    avoidRowCuts: true,
  });

  const handleClick = async () => {
    setFetching(true);
    try {
      const data = await fetchTripDetails(tripId, licenseNo);
      setTrip(data);
      setTimeout(generate, 300);
    } catch (_err) {
      // fetch failed — loading state is reset in finally
    } finally {
      setFetching(false);
    }
  };

  return (
    <>
      {trip && <TripReportTemplate ref={templateRef} trip={trip} />}
      <Button
        type="link"
        icon={<FilePdfOutlined />}
        loading={fetching || loading}
        onClick={handleClick}
        style={{ padding: 0 }}
      >
        {(() => {
          if (fetching) return 'Fetching...';
          if (loading) return 'Generating...';
          return 'Download';
        })()}
      </Button>
    </>
  );
};

TripDetailsReport.defaultProps = { licenseNo: '' };

export default TripDetailsReport;
